import { MirrorData, SetMirrorData, ExtractedMaxLocations } from '@type/job-add';
import { GlobalValuesTypes, RecordOptionsInterface } from '@components/common/Forms';
import { useState, useMemo, useEffect } from 'react';

/** Props */
interface UseJobAddressesProps {
  getFieldsValue: (name: string) => GlobalValuesTypes,
  initialAddresses?: string[],
  initialWorkPlacement?: ApiSchema<'JobModel3'>['workPlacement'],
  inputName?: string,
  reqMirrorData?: Pick<MirrorData, | 'currentActivationsGroup'>,
  setMirrorData?: SetMirrorData,
}

/**
 * @description Process initial addresses. If we leave an empty array it will display nothing.
 * - `initialAddresses` - initial addresses
 * - `initialWorkPlacement` - initial work placement
 */
const processInitialAddresses = (
  initialAddresses: string[] | undefined,
  initialWorkPlacement: ApiSchema<'JobModel3'>['workPlacement'] | undefined,
) => {
  if (initialAddresses && initialAddresses.length && initialWorkPlacement !== 'REMOTE') {
    return initialAddresses;
  }
  return [''];
};

/**
 * @description Extract max locations from each bundle. Keep title and the number of allowed locations.
 * - `bundles` - activation bundles
 * - `currentLocationsNr` - current number of locations
 */
const extractMaxLocations = (
  bundles?: ApiSchema<'ActivationBundle'>[],
  currentLocationsNr?: number,
) => {
  if (!(bundles && currentLocationsNr)) return [];
  const mappedBundles = bundles.map((bundle) => ({
    title: bundle?.title || '',
    maxLocations: bundle?.maxLocations || 50,
  }));
  return mappedBundles.filter((bundle) => bundle.maxLocations < currentLocationsNr);
};

/**
 * @description Job Addresses hook.
 * - `getFieldsValue` - get fields value function from useBjForm
 * - `initialAddresses` - initial addresses; default is empty array
 * - `initialWorkPlacement` - initial work placement; default is 'remote'
 * - `inputName` - name of the input; default is 'addresses'
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData` - function to update the mirror form state
 *
 * **Returns**
 * - `isRemote` - remote state
 * - `setIsRemote` - set remote state
 * - `addresses` - addresses state
 * - `setAddresses` - set addresses state
 * - `addressesRecordOptions` - addresses record options for form. Array of objects with id and defaultValue.
 * - `addAddress` - add new address field
 * - `changeAddress` - change address field
 * - `deleteAddress` - delete address field
 * - `saveAddresses` - save addresses
 * - `generateId` - generate unique id for loop
 */
export const useJobAddresses = (props: UseJobAddressesProps) => {
  // Destructure props
  const {
    getFieldsValue,
    initialAddresses,
    initialWorkPlacement,
    inputName = 'addresses',
    reqMirrorData,
    setMirrorData,
  } = props;

  // Helper states
  const [isRemote, setIsRemote] = useState<boolean>(initialWorkPlacement === 'REMOTE');
  const [addresses, setAddresses] = useState<string[]>(processInitialAddresses(initialAddresses, initialWorkPlacement));
  const [maxLocations, setMaxLocations] = useState<ExtractedMaxLocations[]>(extractMaxLocations(reqMirrorData?.currentActivationsGroup, addresses.length));

  // Reset addresses if initialAddresses change
  useEffect(() => {
    if (initialAddresses) setAddresses(processInitialAddresses(initialAddresses, initialWorkPlacement));
  }, [initialAddresses, initialWorkPlacement]);

  // Update max locations when addresses change
  useEffect(() => {
    if (reqMirrorData?.currentActivationsGroup) {
      setMaxLocations(extractMaxLocations(reqMirrorData?.currentActivationsGroup, addresses.length));
    }
  }, [addresses, reqMirrorData?.currentActivationsGroup]);

  // Addresses record options
  const addressesRecordOptions: RecordOptionsInterface[] = useMemo(() => addresses
    .map((address, index) => ({
      id: `${inputName}_${index}`,
      defaultValue: address,
      valueAsArray: true,
    })), [addresses, inputName]);

  /** For addAddress, deleteAddress and saveAddresses we collect the actual form values
   * before doing the action because we want to have the freshest data, regardless of how
   * the user has interacted with the fields. */

  // Add new address field
  const addAddress = () => {
    const values = getFieldsValue(inputName) as string[];
    setAddresses([...values, '']);
  };

  // Change address field
  const changeAddress = (addressIndex: number, value: string) => {
    const values = getFieldsValue(inputName) as string[];
    values[addressIndex] = value;
    setAddresses(values);
    setMirrorData?.({ [inputName]: values });
  };

  // Delete address field
  const deleteAddress = (addressIndex: number) => {
    const values = getFieldsValue(inputName) as string[];
    values.splice(addressIndex, 1);
    setAddresses(values);
    setMirrorData?.({ [inputName]: values });
  };

  // Save address
  const saveAddresses = () => {
    const values = getFieldsValue(inputName) as string[];
    setAddresses(values);
    setMirrorData?.({ [inputName]: values });
  };

  // Return
  // *******************
  return {
    isRemote,
    setIsRemote,
    addresses,
    setAddresses,
    maxLocations,
    addressesRecordOptions,
    addAddress,
    changeAddress,
    deleteAddress,
    saveAddresses,
  };
};
